import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import { Pause, Play, RotateCcw, RotateCw, VolumeX } from 'lucide-react';
import * as React from 'react';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import useAudioPlayer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/Providers/AudioPlayerProvider/useAudioPlayer';

const stickyStyle = css`
	position: sticky;
	top: 0;
	z-index: 1;
	padding: 0.25rem 0 0.75rem;
	background-color: ${theme.colors.surface};
`;

const barStyle = css`
	display: grid;
	grid-template-columns: auto auto auto auto 1fr auto auto;
	grid-template-areas: 'play back forward elapsed range total rate';
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem 1.25rem;
	border-radius: ${theme.radii.xxlarge};
	background-color: ${theme.colors.surfaceInverted};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: auto auto 1fr auto;
		grid-template-areas:
			'play back forward rate'
			'elapsed range range total';
	}
`;

const noteRowStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

// Filled ONLY on the inverted surface, where `colors.accent` fails contrast: the direction reserves
// `accentBright` for exactly this case, with `inkOnAccentBright` for whatever it fills.
const playButtonStyle = css`
	grid-area: play;
	width: 44px;
	height: 44px;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.accentBright};
	color: ${theme.colors.inkOnAccentBright};
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid ${theme.colors.inkOnDark};
		outline-offset: 2px;
	}
`;

const skipButtonStyle = (area: string) => css`
	grid-area: ${area};
	width: 36px;
	height: 36px;
	border: none;
	border-radius: ${theme.radii.pill};
	background-color: transparent;
	color: ${theme.colors.inkOnDark};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	&:hover:not(:disabled) {
		background-color: color-mix(in srgb, ${theme.colors.inkOnDark} 16%, transparent);
	}

	&:focus-visible {
		outline: 2px solid ${theme.colors.inkOnDark};
		outline-offset: 2px;
	}

	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
`;

const timeStyle = (area: string, color: string) => css`
	grid-area: ${area};
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
	color: ${color};
`;

const rateButtonStyle = css`
	grid-area: rate;
	min-width: 3.5rem;
	padding: 0.375rem 0.625rem;
	border: 1px solid color-mix(in srgb, ${theme.colors.inkOnDark} 32%, transparent);
	border-radius: ${theme.radii.pill};
	background-color: transparent;
	color: ${theme.colors.inkOnDark};
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.medium};
	cursor: pointer;

	&:hover {
		background-color: color-mix(in srgb, ${theme.colors.inkOnDark} 16%, transparent);
	}

	&:focus-visible {
		outline: 2px solid ${theme.colors.inkOnDark};
		outline-offset: 2px;
	}
`;

const THUMB_SIZE = '14px';

const trackRule = `
	height: 6px;
	border-radius: ${theme.radii.pill};
	background: linear-gradient(
		to right,
		${theme.colors.accentBright} var(--fill),
		color-mix(in srgb, ${theme.colors.inkOnDark} 16%, transparent) var(--fill)
	);
`;

const thumbRule = `
	width: ${THUMB_SIZE};
	height: ${THUMB_SIZE};
	border: none;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.inkOnDark};
`;

// The thumb stays hidden at rest and appears on hover or keyboard focus, so the bar reads as the
// plain progress track it replaces until the user reaches for it.
const rangeStyle = (percentage: number) => css`
	grid-area: range;
	--fill: ${percentage}%;
	width: 100%;
	min-width: 0;
	height: ${THUMB_SIZE};
	margin: 0;
	appearance: none;
	background: transparent;
	cursor: pointer;

	&::-webkit-slider-runnable-track {
		${trackRule}
	}

	&::-moz-range-track {
		${trackRule}
	}

	&::-webkit-slider-thumb {
		appearance: none;
		margin-top: -4px;
		${thumbRule}
		opacity: 0;
	}

	&::-moz-range-thumb {
		${thumbRule}
		opacity: 0;
	}

	&:hover::-webkit-slider-thumb,
	&:focus-visible::-webkit-slider-thumb {
		opacity: 1;
	}

	&:hover::-moz-range-thumb,
	&:focus-visible::-moz-range-thumb {
		opacity: 1;
	}

	&:focus-visible {
		outline: 2px solid ${theme.colors.inkOnDark};
		outline-offset: 4px;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;

const captionStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkSecondary};
`;

const unavailableRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 1rem 1.25rem;
	border-radius: ${theme.radii.large};
	background-color: ${theme.colors.surfaceMuted};
	color: ${theme.colors.inkSecondary};
`;

const unavailableTextStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
`;

const visuallyHiddenStyle = css`
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	clip: rect(0 0 0 0);
`;

const SKIP_SECONDS = 10;

const pad = (value: number): string => String(value).padStart(2, '0');

const formatSeconds = (totalSeconds: number): string => {
	const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, totalSeconds) : 0;
	const minutes = Math.floor(safeSeconds / 60);
	const seconds = Math.floor(safeSeconds % 60);

	return `${pad(minutes)}:${pad(seconds)}`;
};

const formatRate = (rate: number): string => `${String(rate).replace('.', ',')}×`;

/**
 * Mounts the screen's single `<audio>` element only when `isAvailable`: no element, no request,
 * when the recording is gone or still uploading (§9.4). Position and skips need a seekable
 * recording; the limitation note (`duration > 0`, the marker that `loadedmetadata` has already
 * fired) is shown once under the sticky bar rather than per turn — it describes the media
 * container, not any one turn.
 */
type Props = {
	readonly hasSpeakers: boolean;
};

const TranscriptPlayerBar: React.FC<Props> = ({ hasSpeakers }) => {
	const {
		audioSrc,
		isPlaying,
		audioRef,
		duration,
		currentTime,
		playbackRate,
		togglePlay,
		seekTo,
		skipBy,
		cyclePlaybackRate,
		isAvailable,
		isPositionable,
	} = useAudioPlayer();

	const handleSeek = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			seekTo(Number(event.target.value));
		},
		[seekTo],
	);

	const handleSkipBack = React.useCallback((): void => skipBy(-SKIP_SECONDS), [skipBy]);
	const handleSkipForward = React.useCallback((): void => skipBy(SKIP_SECONDS), [skipBy]);

	if (!isAvailable || audioSrc === null) {
		return (
			<div css={unavailableRowStyle}>
				<VolumeX size={20} aria-hidden="true" />
				<p css={unavailableTextStyle}>
					L'enregistrement n'est plus disponible. Le transcript reste modifiable.
				</p>
			</div>
		);
	}

	const percentage = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
	const showsPositioningLimitation = duration > 0 && !isPositionable;
	const rateLabel = formatRate(playbackRate);

	return (
		<React.Fragment>
			<div css={stickyStyle}>
				<div css={barStyle}>
					<audio ref={audioRef} src={audioSrc} preload="metadata" crossOrigin="use-credentials">
						<track kind="captions" />
					</audio>
					<button type="button" css={playButtonStyle} onClick={togglePlay}>
						{isPlaying ? (
							<Pause size={16} aria-hidden="true" />
						) : (
							<Play size={16} aria-hidden="true" />
						)}
						<span css={visuallyHiddenStyle}>{isPlaying ? 'Mettre en pause' : 'Lire'}</span>
					</button>
					<button
						type="button"
						css={skipButtonStyle('back')}
						aria-label="Reculer de 10 secondes"
						disabled={!isPositionable}
						onClick={handleSkipBack}
					>
						<RotateCcw size={16} aria-hidden="true" />
					</button>
					<button
						type="button"
						css={skipButtonStyle('forward')}
						aria-label="Avancer de 10 secondes"
						disabled={!isPositionable}
						onClick={handleSkipForward}
					>
						<RotateCw size={16} aria-hidden="true" />
					</button>
					<span css={timeStyle('elapsed', theme.colors.inkOnDark)}>
						{formatSeconds(currentTime)}
					</span>
					<input
						type="range"
						css={rangeStyle(percentage)}
						min={0}
						max={duration}
						step={0.1}
						value={Math.min(currentTime, duration)}
						disabled={!isPositionable || duration === 0}
						aria-label="Position de lecture"
						aria-valuetext={`${formatSeconds(currentTime)} sur ${formatSeconds(duration)}`}
						onChange={handleSeek}
					/>
					<span css={timeStyle('total', theme.colors.inkOnDarkMuted)}>
						{formatSeconds(duration)}
					</span>
					<button
						type="button"
						css={rateButtonStyle}
						aria-label={`Vitesse de lecture : ${rateLabel}`}
						onClick={cyclePlaybackRate}
					>
						{rateLabel}
					</button>
				</div>
			</div>
			<ConditionalLoader data={hasSpeakers || showsPositioningLimitation}>
				<div css={noteRowStyle}>
					<ConditionalLoader data={hasSpeakers}>
						<span css={captionStyle}>Cliquez un nom pour attribuer le locuteur</span>
					</ConditionalLoader>
					<ConditionalLoader data={showsPositioningLimitation}>
						<span css={captionStyle}>
							Le positionnement précis n'est pas disponible pour cet enregistrement. La lecture
							reste possible.
						</span>
					</ConditionalLoader>
				</div>
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default TranscriptPlayerBar;
